import load from '#core/load';
import g from '#core/global';


const tooltip_$ = '.tooltip';
const trigger_$ = '.tooltip__trigger';
const content_$ = '.tooltip__content';
const hidden_c = 'tooltip__content--hidden';
const below_c = 'tooltip__content--below';
const offset_prop = '--tooltip-offset-x';
const ready_attr = 'data-tooltip-ready';
const viewport_padding_x = 20;
const viewport_padding_top = 80;
const hide_delay = 200;

let global_listeners_attached = false;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('tooltip')) return false;

		load.content(() => {
			init_all();
		});
	},

};

export default module;
module.init();


/**
 * Initialize all triggers in the document and attach global listeners.
 * Idempotent — safe to call multiple times (e.g. after dynamic DOM updates).
 */
export function init_all() {
	const $triggers = document.querySelectorAll(trigger_$);
	$triggers.forEach(init_trigger);
	init_global_listeners();
}


/**
 * Initialize a single tooltip trigger. The trigger's next sibling is expected
 * to be the tooltip content element. Idempotent.
 *
 * @param {HTMLElement} $trigger
 */
export function init_trigger($trigger) {
	if ($trigger.getAttribute(ready_attr) === g.true_s) return;

	const $content = $trigger.nextElementSibling;
	if (!$content || !$content.matches(content_$)) return;

	$trigger.setAttribute(ready_attr, g.true_s);

	let hide_timeout = null;

	const cancel_hide = () => {
		if (hide_timeout) {
			clearTimeout(hide_timeout);
			hide_timeout = null;
		}
	};

	const show_tooltip = () => {
		cancel_hide();
		$content.classList.remove(hidden_c);
		position_tooltip($trigger, $content);
	};

	const hide_tooltip = () => {
		cancel_hide();
		$content.classList.add(hidden_c);
		reset_tooltip_position($content);
	};

	const queue_hide = () => {
		cancel_hide();
		hide_timeout = setTimeout(hide_tooltip, hide_delay);
	};

	const toggle_tooltip = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const is_hidden = $content.classList.contains(hidden_c);
		close_all_tooltips();
		if (is_hidden) show_tooltip();
		else hide_tooltip();
	};

	// Hover: show on enter, hide on leave (with delay so the user can move
	// from trigger to tooltip body without it disappearing immediately)
	$trigger.addEventListener(g.mouse_enter_e, show_tooltip);
	$trigger.addEventListener(g.mouse_leave_e, queue_hide);
	$content.addEventListener(g.mouse_enter_e, cancel_hide);
	$content.addEventListener(g.mouse_leave_e, queue_hide);

	// Keyboard: show on focus, hide on blur (no delay needed)
	$trigger.addEventListener(g.focus_e, show_tooltip);
	$trigger.addEventListener(g.blur_e, hide_tooltip);

	// Touch / non-anchor click: toggle
	$trigger.addEventListener(g.click_e, (e) => {
		// If the trigger is a link and the tooltip is already open, allow
		// the link to navigate normally
		if (
			$trigger.tagName === 'A'
			&& $trigger.hasAttribute('href')
			&& !$content.classList.contains(hidden_c)
		) {
			return;
		}
		toggle_tooltip(e);
	});

	// Keyboard activation for non-anchor (role=button) triggers
	if ($trigger.tagName !== 'A') {
		$trigger.addEventListener(g.key_down_e, (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				toggle_tooltip(e);
			}
		});
	}
}


/**
 * Attach document-level listeners for closing tooltips. Idempotent.
 */
export function init_global_listeners() {
	if (global_listeners_attached) return;
	global_listeners_attached = true;

	document.addEventListener(g.click_e, (e) => {
		if (!e.target.closest(tooltip_$)) close_all_tooltips();
	});
	document.addEventListener(g.key_down_e, (e) => {
		if (e.key === 'Escape') close_all_tooltips();
	});
}


/**
 * Position tooltip within viewport bounds
 * @param {HTMLElement} $trigger
 * @param {HTMLElement} $content
 */
function position_tooltip($trigger, $content) {
	const trigger_rect = $trigger.getBoundingClientRect();
	const viewport_width = window.innerWidth;

	const space_above = trigger_rect.top;
	const show_below = space_above < viewport_padding_top;

	if (show_below) $content.classList.add(below_c);
	else $content.classList.remove(below_c);

	const trigger_center_x = trigger_rect.left + (trigger_rect.width / 2);

	const tooltip_rect = $content.getBoundingClientRect();
	const tooltip_width = tooltip_rect.width;

	const tooltip_left_edge = trigger_center_x - (tooltip_width / 2);
	const tooltip_right_edge = trigger_center_x + (tooltip_width / 2);

	let offset_x = 0;
	if (tooltip_left_edge < viewport_padding_x) {
		offset_x = viewport_padding_x - tooltip_left_edge;
	} else if (tooltip_right_edge > viewport_width - viewport_padding_x) {
		offset_x = (viewport_width - viewport_padding_x) - tooltip_right_edge;
	}

	if (offset_x !== 0) {
		$content.style.setProperty(offset_prop, `${offset_x}px`);
	} else {
		$content.style.removeProperty(offset_prop);
	}
}


/**
 * Reset tooltip positioning to default (clears horizontal offset and the
 * "below trigger" class)
 * @param {HTMLElement} $tooltip
 */
function reset_tooltip_position($tooltip) {
	$tooltip.classList.remove(below_c);
	$tooltip.style.removeProperty(offset_prop);
}


/**
 * Close all open tooltips in the document and reset their positions
 */
function close_all_tooltips() {
	document.querySelectorAll(content_$).forEach(($tooltip) => {
		$tooltip.classList.add(hidden_c);
		reset_tooltip_position($tooltip);
	});
}
